import { useWSQueries } from "@ws-react-query";
import {
  BackgroundCheckConfiguration,
  DocumentTemplate,
  InsuranceMonitorConfiguration,
  RequirementType,
  SharedFileRequestConfiguration
} from "@wingspanhq/wingspan-api-sdk";

import {
  backgroundCheckConfigurationsApi,
  documentTemplatesApi,
  insuranceMonitorConfigurationsApi,
  sharedFileRequestConfigurationsApi
} from "@api";
import { queriesBackgroundCheckConfigurations } from "../../../v3/query/queriesBackgroundCheckConfigurations";
import { queriesDocumentTemplates } from "../../../v3/query/queriesDocumentTemplates";
import { queriesInsuranceMonitorConfigurations } from "../../../v3/query/queriesInsuranceMonitorConfigurations";
import { queriesSharedFileRequestConfigurations } from "../../../v3/query/queriesSharedFileRequestConfigurations";

type RequirementDefinitionBase = {
  dataSourceId?: string | null;
  type?: RequirementType;
};

export type GetRequirementDefinitionDataSourceSettingsParams = {
  requirementDefinitions: RequirementDefinitionBase[];
  fetch: "all" | "contextType";
  enabled?: boolean;
};

export type RequirementDefinitionDataSourceSettingsItem =
  | DocumentTemplate
  | SharedFileRequestConfiguration
  | BackgroundCheckConfiguration
  | InsuranceMonitorConfiguration;

/**
 * The v3 template carries only a `dataSourceId`; the resource it points at is
 * implied by the requirement `type` (v1 spelled this out as `dataSourceType`).
 * Only these four types have a settings resource — every other type resolves to
 * no query at all.
 *
 * Signature reads the v3 document-template resource, DocumentUpload the v3
 * shared-file-request configuration, BackgroundCheck the v3 background-check
 * configuration and InsuranceCoverage the v3 monitor configuration
 * (TODO(v3-verify): all four assume v1-authored ids resolve on the v3
 * endpoints — same backing store per the API design docs).
 *
 * The background-check branch previously read the background *check* resource
 * with what is a *configuration* id — the wrong resource for a definition's
 * `dataSourceId`.
 */
const configs: Partial<
  Record<
    RequirementType,
    {
      getQueryKey: (dataSourceId: string) => unknown[];
      queryFn: (dataSourceId: string) => Promise<unknown>;
    }
  >
> = {
  [RequirementType.SIGNATURE]: {
    getQueryKey: dataSourceId =>
      queriesDocumentTemplates.getDocumentTemplate.getQueryKey({
        templateId: dataSourceId
      }) as unknown[],
    queryFn: dataSourceId =>
      documentTemplatesApi.getDocumentTemplate({ templateId: dataSourceId })
  },
  [RequirementType.BACKGROUND_CHECK]: {
    getQueryKey: dataSourceId =>
      queriesBackgroundCheckConfigurations.getBackgroundCheckConfiguration.getQueryKey(
        { backgroundCheckConfigurationId: dataSourceId }
      ) as unknown[],
    queryFn: dataSourceId =>
      backgroundCheckConfigurationsApi.getBackgroundCheckConfiguration({
        backgroundCheckConfigurationId: dataSourceId
      })
  },
  [RequirementType.DOCUMENT_UPLOAD]: {
    getQueryKey: dataSourceId =>
      queriesSharedFileRequestConfigurations.getSharedFileRequestConfiguration.getQueryKey(
        { sharedFileRequestConfigurationId: dataSourceId }
      ) as unknown[],
    queryFn: dataSourceId =>
      sharedFileRequestConfigurationsApi.getSharedFileRequestConfiguration({
        sharedFileRequestConfigurationId: dataSourceId
      })
  },
  [RequirementType.INSURANCE_COVERAGE]: {
    getQueryKey: dataSourceId =>
      queriesInsuranceMonitorConfigurations.getInsuranceMonitorConfiguration.getQueryKey(
        { insuranceMonitorConfigurationId: dataSourceId }
      ) as unknown[],
    queryFn: dataSourceId =>
      insuranceMonitorConfigurationsApi.getInsuranceMonitorConfiguration({
        insuranceMonitorConfigurationId: dataSourceId
      })
  }
};

export const useQueryRequirementDefinitionsSettings = (
  params: GetRequirementDefinitionDataSourceSettingsParams
) => {
  const {
    requirementDefinitions = [],
    enabled = true,
    fetch = "contextType"
  } = params;

  const queryConfigs = requirementDefinitions
    .filter(requirementDefinition => requirementDefinition.dataSourceId)
    .filter(requirementDefinition =>
      fetch === "all"
        ? true
        : fetch === "contextType"
          ? requirementDefinition.type === RequirementType.DOCUMENT_UPLOAD ||
            requirementDefinition.type === RequirementType.INSURANCE_COVERAGE
          : false
    )
    .filter(
      requirementDefinition =>
        requirementDefinition.type && configs[requirementDefinition.type]
    )
    .map(requirementDefinition => {
      const config = configs[requirementDefinition.type!]!;
      const dataSourceId = requirementDefinition.dataSourceId!;

      return {
        dataSourceId,
        queryKey: config.getQueryKey(dataSourceId),
        queryFn: () => config.queryFn(dataSourceId),
        retry: false,
        enabled
      };
    }) as any;

  const queries = useWSQueries({ queries: queryConfigs });

  const isLoading = queries.some(query => query.isLoading);
  const isFetching = queries.some(query => query.isFetching);
  const isFetched = queries.every(query => query.isFetched);

  // pair each result with its own query config BEFORE dropping misses — the
  // previous filter-then-index-into-configs zip misaligned ids when any
  // query returned undefined
  const resolved = queries
    .map((query, index) => ({
      dataSourceId: queryConfigs[index].dataSourceId as string,
      item: query.data as
        | RequirementDefinitionDataSourceSettingsItem
        | undefined
    }))
    .filter(
      (
        entry
      ): entry is {
        dataSourceId: string;
        item: RequirementDefinitionDataSourceSettingsItem;
      } => !!entry.item
    );

  return {
    isLoading,
    isFetching,
    isFetched,
    list: resolved.map(entry => entry.item),
    data: resolved.reduce(
      (acc, entry) => {
        acc[entry.dataSourceId] = entry.item;
        return acc;
      },
      {} as Record<string, RequirementDefinitionDataSourceSettingsItem>
    )
  };
};
